import { useEffect, useState } from "react";
import { SiteAnchorVisual } from "../../../visuals/SiteAnchorVisual";
import { WorldRenderer } from "../../WorldRenderer";
import { WREvents } from "../../WREvents";
import { loadRecords, saveModel } from "../../../api/connection";
import { createScenario, Scenario } from "../../../models/Scenario";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { PlayCircle, StopCircle } from "@mui/icons-material";
import { getCreds } from "../../../contexts/PageContext";
import { ScenarioClient } from "../scenarios/ScenarioClient";
import { RenderAccordionToolbar } from "../../../components/Minibar";

export function ScenarioList({
  wr,
  pinned,
  panel,
  togglePin,
}: {
  wr: WorldRenderer;
  pinned: Set<string>;
  panel: string;
  togglePin: (panel: string) => void;
}) {
  const [site, setSite] = useState<SiteAnchorVisual | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(
    null
  );
  const [client, setClient] = useState<ScenarioClient>();

  useEffect(() => {
    const siteActivatedListener = async (site: SiteAnchorVisual) => {
      setSite(site);
      const scenarios = await loadRecords<Scenario>("scenarios", {
        siteId: site._id,
      });
      setScenarios(scenarios);
    };
    wr.onEvent(WREvents.SiteActivated, siteActivatedListener);
    const siteDeactivatedListener = () => {
      setSite(null);
    };
    wr.onEvent(WREvents.SiteDeactivated, siteDeactivatedListener);
    if (wr.dataService.activeSite) {
      siteActivatedListener(wr.dataService.activeSite);
    }

    return () => {
      wr.off(WREvents.SiteActivated, siteActivatedListener);
      wr.off(WREvents.SiteDeactivated, siteDeactivatedListener);
    };
  }, [wr]);

  useEffect(() => {
    if (!client) {
      return;
    }
    client.on("status", (status) => {
      console.log(`ScenarioClient status: ${status}`);
      if (status === "completed") {
        setClient(undefined);
      }
    });
  }, [client]);

  return (
    <>
      <RenderAccordionToolbar
        pinned={pinned}
        panel={panel}
        togglePin={togglePin}
      />
      <Box>
        <List>
          {scenarios.map((scenario) => (
            <ListItem key={scenario._id} disablePadding dense>
              <ListItemButton
                onClick={() => {
                  setSelectedScenario(scenario);
                  wr.emitEvent(WREvents.ScenarioSelected, scenario);
                }}
              >
                <ListItemText primary={scenario.name} />
              </ListItemButton>
              <ListItemIcon sx={{ minWidth: "0px" }}>
                <Tooltip title="Run" placement="top">
                  <IconButton
                    disabled={client !== undefined}
                    size="small"
                    onClick={async () => {
                      // const agentMap = new Map<string, BaseAgent>();
                      // const agents = getAgents();
                      // for (const agent of agents) {
                      //   agentMap.set(agent.visual._id, agent);
                      // }
                      const client = new ScenarioClient(wr, { ...scenario! });
                      setClient(client);
                      await client.start();
                    }}
                  >
                    <PlayCircle />
                  </IconButton>
                </Tooltip>
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: "0px" }}>
                <Tooltip title="Stop" placement="top">
                  <IconButton
                    disabled={client === undefined}
                    size="small"
                    onClick={async () => {
                      client?.stop();
                      setClient(undefined);
                    }}
                  >
                    <StopCircle />
                  </IconButton>
                </Tooltip>
              </ListItemIcon>
            </ListItem>
          ))}
        </List>
        <Box>
          <Button
            color="secondary"
            onClick={async () => {
              const scenario = createScenario();
              const { user, org } = getCreds();
              scenario.orgId = org?._id || "";
              scenario.siteId = site?._id || "";
              await saveModel(scenario);
              console.log({ scenario });
              setScenarios([...scenarios, scenario]);
            }}
          >
            Add
          </Button>
        </Box>
      </Box>
    </>
  );
}
